import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChefHat, Clock, Check, RotateCcw, AlertTriangle } from 'lucide-react';

function elapsed(from) {
  const s = Math.floor((Date.now() - new Date(from).getTime()) / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

function slaClass(from) {
  const m = (Date.now() - new Date(from).getTime()) / 60000;
  if (m > 12) return 'border-l-destructive';
  if (m > 6) return 'border-l-warning';
  return 'border-l-success';
}

export default function KDS() {
  const { currentOutlet, outlets, user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [prepAlerts, setPrepAlerts] = useState([]);
  const [, setTick] = useState(0);
  const outletId = currentOutlet?.id || outlets?.[0]?.id;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/kds/tickets', { params: outletId ? { outlet_id: outletId } : {} });
      setTickets(data);
    } catch (e) {}
    try { const p = await api.get('/prep-alerts'); setPrepAlerts(p.data); } catch (e) {}
  }, [outletId]);

  useEffect(() => {
    document.documentElement.classList.add('dark');
    load();
    const poll = setInterval(load, 4000);
    const timer = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(poll); clearInterval(timer); document.documentElement.classList.remove('dark'); };
  }, [load]);

  const bump = async (id) => { await api.post(`/kds/tickets/${id}/bump`); load(); };
  const recall = async (id) => { await api.post(`/kds/tickets/${id}/recall`); load(); };

  return (
    <div className="min-h-screen bg-background text-foreground p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center"><ChefHat className="h-5 w-5 text-primary-foreground" /></div>
          <div>
            <h1 className="font-display text-xl font-bold">Kitchen Display</h1>
            <p className="text-xs text-muted-foreground">{currentOutlet?.name || 'All stations'} · {tickets.length} active</p>
          </div>
        </div>
        <div className="flex gap-3 text-xs">
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-success" /> On time</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-warning" /> Watch</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-destructive" /> Late</span>
        </div>
      </div>

      {prepAlerts.length > 0 && (
        <div className="mb-4 rounded-xl border border-destructive/40 bg-destructive/10 p-3" data-testid="prep-alert-banner">
          <div className="flex items-center gap-2 text-destructive font-semibold text-sm mb-1">
            <AlertTriangle className="h-4 w-4" /> Prep needed — {prepAlerts.length} sub-recipe{prepAlerts.length > 1 ? 's' : ''} low
          </div>
          <div className="flex flex-wrap gap-1.5">
            {prepAlerts.slice(0, 10).map((s) => (
              <span key={s.id} className="text-xs px-2 py-1 rounded-full bg-background/60 border">{s.name}: <b>{s.stock} {s.yield_unit}</b> left</span>
            ))}
            {prepAlerts.length > 10 && <span className="text-xs px-2 py-1 text-muted-foreground">+{prepAlerts.length - 10} more</span>}
          </div>
        </div>
      )}

      {tickets.length === 0 ? (
        <div className="h-[70vh] flex flex-col items-center justify-center text-muted-foreground">
          <ChefHat className="h-16 w-16 mb-3 opacity-30" />
          <p>No active tickets. New orders appear here automatically.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {tickets.map((t) => (
            <div key={t.id} data-testid={`kds-ticket-card-${t.kot_no}`} className={`rounded-2xl border border-border border-l-4 ${slaClass(t.created_at)} bg-card p-4 flex flex-col`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-mono font-bold">{t.kot_no}</div>
                  <Badge variant="secondary" className="text-[10px] capitalize">{t.type}{t.table_id ? '' : ''}</Badge>
                </div>
                <div className="flex items-center gap-1 font-mono text-lg"><Clock className="h-4 w-4" />{elapsed(t.created_at)}</div>
              </div>
              <div className="flex-1 space-y-1 mb-3">
                {t.items.map((it, i) => (
                  <div key={i} className="text-sm">
                    <span className="font-bold text-primary">{it.qty}×</span> {it.name}
                    {it.notes && <div className="text-xs text-warning ml-5">↳ {it.notes}</div>}
                  </div>
                ))}
                {t.notes && (
                  <div className="mt-2 rounded-lg bg-warning/15 border border-warning/40 px-2 py-1.5 text-xs text-warning">
                    <b>Order note:</b> {t.notes}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button data-testid={`kds-ticket-bump-button-${t.kot_no}`} className="flex-1" onClick={() => bump(t.id)}><Check className="h-4 w-4 mr-1" /> Bump</Button>
                <Button variant="outline" size="icon" onClick={() => recall(t.id)}><RotateCcw className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
